/* bb-budget.core.js (DROP-IN REPLACEMENT)
   - Core = state + parsing + calculations + master build/export helpers
   - Depends on: bb-budget.util.js -> TYBH.util
*/

;(() => {
  window.TYBH = window.TYBH || {};
  const TYBH = window.TYBH;
  const U = TYBH.util;

  if (!U) {
    console.error("[TYBH] util missing. Load bb-budget.util.js before core/ui.");
    return;
  }

  const {
    stripBom, parseMaster, readKeyValueBlock, parseCsv,
    num, isoToday, ymToday, toMonth, uid, uniq,
    downloadText
  } = U;

  const DEFAULT_HEADER_TX = [
    "ID","Date","Month","Type","Person","Account","Category","Subcategory","Item","Payee","Amount","Notes",
    "Impulse","ImpulseReason","Flags"
  ];

  // ✅ legacy compatibility (UI / older HTML may read these directly)
  TYBH.DEFAULT_HEADER_TX = TYBH.DEFAULT_HEADER_TX || DEFAULT_HEADER_TX;

  const state = TYBH.state = TYBH.state || {
    loaded: false,
    settings: { currency:"USD", people:["Tin Lei","Tin Wing"] },
    rules: {
      mode: "BLOCK",
      eatCats: "Eat Out, Dining, Restaurant, Takeout",
      eatWeekly: 120,
      impulseWeekly: 80,
      nonEssCats: "Fun, Shopping, Entertainment",
      nonEssDaily: 40,
    },
    grow: { investMonthly: 150, investAccount: "Brokerage" },
    accounts: [],
    bills: [],
    move: [],
    budgets: [],
    goals: [],
    categories: [],
    tx: []
  };

  function resetDataArrays(){
    state.accounts = [];
    state.bills = [];
    state.move = [];
    state.budgets = [];
    state.goals = [];
    state.categories = [];
    state.tx = [];
  }

  function allMonths(){
    const ms = uniq((state.tx||[]).map(t=>t.Month).filter(Boolean).concat([ymToday()]));
    ms.sort((a,b)=>b.localeCompare(a));
    return ms;
  }

  function monthTx(month){
    const m = String(month||"").trim();
    return m ? state.tx.filter(t=>t.Month===m) : [...state.tx];
  }

  function mergeAccountsByName(){
    const map = new Map();
    const out = [];
    for(const a0 of (state.accounts||[])){
      const name = String(a0.Account||"").trim();
      if(!name) continue;

      const a = {
        Account: name,
        Owner: String(a0.Owner||""),
        Type: String(a0.Type||""),
        Nature: String(a0.Nature||"ASSET").toUpperCase()==="LIABILITY" ? "LIABILITY" : "ASSET",
        StartBalance: num(a0.StartBalance),
        BankBalance: (String(a0.BankBalance??"").trim()==="" ? "" : num(a0.BankBalance)),
        Active: String(a0.Active||"Y")
      };

      if(!map.has(name)){
        map.set(name, a);
        out.push(a);
      }else{
        const t = map.get(name);
        t.StartBalance = num(t.StartBalance) + num(a.StartBalance);

        if((t.BankBalance==="" || t.BankBalance==null) && String(a.BankBalance??"").trim()!==""){
          t.BankBalance = num(a.BankBalance);
        }

        if(!t.Owner && a.Owner) t.Owner = a.Owner;
        if(!t.Type && a.Type) t.Type = a.Type;
        if(!t.Nature && a.Nature) t.Nature = a.Nature;

        const tN = String(t.Active||"Y").trim().toUpperCase()==="N";
        const aN = String(a.Active||"Y").trim().toUpperCase()==="N";
        if(tN && !aN) t.Active = a.Active;
      }
    }
    state.accounts = out;
  }

  function loadTransactionsCsv(text, opts){
    opts = opts || {};
    text = stripBom(text);
    const rows = parseCsv((text.trim()+"\n"));
    if(!rows.length) throw new Error("CSV is empty.");

    const headRaw = (rows[0]||[]).map(h=>String(h||"").trim());
    if(headRaw[0]) headRaw[0] = headRaw[0].replace(/^\uFEFF/,"");

    function normKey(x){
      return String(x || "").toLowerCase().trim().replace(/[^a-z0-9]+/g,"");
    }

    const headMap = {};
    const headKeys = [];
    for(let i=0;i<headRaw.length;i++){
      const k = normKey(headRaw[i]);
      if(!k) continue;
      if(headMap[k] === undefined){
        headMap[k] = i;
        headKeys.push(k);
      }
    }

    function findIndex(name){
      const t = normKey(name);
      if(headMap[t] !== undefined) return headMap[t];
      for(const k of headKeys){ if(k === t) return headMap[k]; }
      for(const k of headKeys){ if(k.startsWith(t) || k.includes(t)) return headMap[k]; }
      return undefined;
    }

    const get = (row, name)=>{
      const i = findIndex(name);
      return (i !== undefined) ? (row[i] ?? "") : "";
    };

    const getAny = (row, names)=>{
      for(const n of names){
        const v = get(row, n);
        if(String(v ?? "").trim() !== "") return v;
      }
      return "";
    };

    const hasID = findIndex("ID") !== undefined;
    const hasDate = findIndex("Date") !== undefined;

    const out = [];
    for(let i=1;i<rows.length;i++){
      const r = rows[i];
      if(!r || !r.length) continue;

      const rowHasAny = r.some(c=>String(c ?? "").trim() !== "");
      if(!rowHasAny) continue;

      const rawID = hasID ? getAny(r, ["ID","TxID","TransactionID"]) : (r[0]||"");
      const rawDate = hasDate ? getAny(r, ["Date","TransactionDate","TxDate"]) : (r[1]||"");

      const obj = {
        ID: String(rawID||"").trim(),
        Date: String(rawDate||"").trim().slice(0,10),
        Month: String(getAny(r, ["Month"])) || "",
        Type: String(getAny(r, ["Type"])) || "",
        Person: String(getAny(r, ["Person","Name","Owner"])) || "",
        Account: String(getAny(r, ["Account","Acct","AccountName"])) || "",
        Category: String(getAny(r, ["Category","Cat"])) || "",
        Subcategory: String(getAny(r, ["Subcategory","SubCat","SubcategoryName"])) || "",
        Item: String(getAny(r, ["Item","Code","Template"])) || "",
        Payee: String(getAny(r, ["Payee","Merchant","Vendor"])) || "",
        Amount: num(getAny(r, ["Amount","Amt","Value","Total"])),
        Notes: String(getAny(r, ["Notes","Memo","Description"])) || "",
        Impulse: String(getAny(r, ["Impulse","ImpulseFlag"])) || "N",
        ImpulseReason: String(getAny(r, ["ImpulseReason","Reason"])) || "",
        Scope: String(getAny(r, ["Scope","BusinessPersonal","BP","Use","UseType"])) || "",
        Receipt: String(getAny(r, ["Receipt","ReceiptURL","ReceiptLink","Link","URL"])) || "",
        Flags: String(getAny(r, ["Flags","Flag","Tags"])) || ""
      };

      obj.ID = obj.ID || uid();
      obj.Date = (obj.Date || isoToday()).slice(0,10);

      // ✅ keep Month if provided, else derive from Date
      obj.Month = String(obj.Month||"").trim() || toMonth(obj.Date);

      obj.Type = (obj.Type==="Income" || obj.Type==="Expense") ? obj.Type : "Expense";
      obj.Amount = Math.abs(num(obj.Amount));
      obj.Impulse = (String(obj.Impulse||"N").toUpperCase()==="Y") ? "Y" : "N";
      obj.Scope = (String(obj.Scope||"").trim().toLowerCase()==="business") ? "Business" : (String(obj.Scope||"").trim() ? "Personal" : "Personal");
      obj.Receipt = String(obj.Receipt||"").trim();

      const meaningful =
        (String(obj.Person||obj.Account||obj.Category||obj.Subcategory||obj.Item||obj.Payee||obj.Notes||"").trim() !== "") ||
        (obj.Amount > 0);

      if(!meaningful) continue;
      out.push(obj);
    }

  // optional de-dup (for messy bank exports / repeated imports)
  if(opts.dedup){
    const seen = new Set();
    const deduped = [];
    for(const t of out){
      const key = (t.ID && String(t.ID).trim()) ? ("id:"+String(t.ID).trim()) : (
        [
          String(t.Date||""),
          String(t.Person||""),
          String(t.Account||""),
          String(t.Type||""),
          String(t.Category||""),
          String(t.Subcategory||""),
          String(t.Item||""),
          String(t.Payee||""),
          // amount normalized
          (Number(t.Amount||0).toFixed(2)),
          String(t.Notes||"")
        ].map(s=>String(s).toLowerCase().trim().replace(/\s+/g," ")).join("|")
      );
      if(seen.has(key)) continue;
      seen.add(key);
      deduped.push(t);
    }
    out.length = 0;
    Array.prototype.push.apply(out, deduped);
  }

  state.tx = out;
}

  function loadFromMaster(text, opts){
    opts = opts || {};
    text = stripBom(text);
    const blocks = parseMaster(text);

    resetDataArrays();

    // If not a master file, treat as tx-only
    if(!Object.keys(blocks).length){
      loadTransactionsCsv(text, opts);
      state.loaded = true;
      mergeAccountsByName();
      return;
    }

    if(blocks.Settings){
      const map = readKeyValueBlock(blocks.Settings);
      const currency = map.Currency || "USD";
      const people = (map.People || "Tin Lei,Tin Wing").split(",").map(s=>s.trim()).filter(Boolean);
      state.settings.currency = currency;
      state.settings.people = people.length ? people : ["Tin Lei","Tin Wing"];
    }

    if(blocks.Rules){
      const map = readKeyValueBlock(blocks.Rules);
      state.rules.mode = (map.Mode || state.rules.mode || "BLOCK").toUpperCase();
      state.rules.eatCats = map.EatCats || state.rules.eatCats;
      state.rules.eatWeekly = num(map.EatWeekly || state.rules.eatWeekly);
      state.rules.impulseWeekly = num(map.ImpulseWeekly || state.rules.impulseWeekly);
      state.rules.nonEssCats = map.NonEssCats || state.rules.nonEssCats;
      state.rules.nonEssDaily = num(map.NonEssDaily || state.rules.nonEssDaily);

      state.grow.investMonthly = num(map.InvestMonthly || state.grow.investMonthly);
      state.grow.investAccount = map.InvestAccount || state.grow.investAccount;
    }

    if(blocks.Accounts){
      const rows = parseCsv(blocks.Accounts);
      const head = rows[0] || [];
      const idx = (name)=>head.indexOf(name);
      for(let i=1;i<rows.length;i++){
        const r = rows[i];
        if(!r || !r.length) continue;
        const a = {
          Account: r[idx("Account")] || "",
          Owner: r[idx("Owner")] || "",
          Type: r[idx("Type")] || "",
          Nature: (r[idx("Nature")] || "ASSET"),
          StartBalance: num(r[idx("StartBalance")]),
          BankBalance: (r[idx("BankBalance")] === "" ? "" : num(r[idx("BankBalance")])),
          Active: (r[idx("Active")] || "Y")
        };
        if(String(a.Account).trim()) state.accounts.push(a);
      }
    }

    if(blocks.Bills){
      const rows = parseCsv(blocks.Bills);
      const head = rows[0] || [];
      const idx = (name)=>head.indexOf(name);
      for(let i=1;i<rows.length;i++){
        const r = rows[i];
        if(!r || !r.length) continue;
        const b = {
          BillName: r[idx("BillName")] || "",
          Amount: num(r[idx("Amount")]),
          DueDay: num(r[idx("DueDay")]),
          Account: r[idx("Account")] || "",
          AutoPay: (r[idx("AutoPay")] || "N"),
          Notes: r[idx("Notes")] || "",
          Active: (r[idx("Active")] || "Y")
        };
        if(String(b.BillName).trim()) state.bills.push(b);
      }
    }

    if(blocks.Move){
      const rows = parseCsv(blocks.Move);
      const head = rows[0] || [];
      const idx = (name)=>head.indexOf(name);
      for(let i=1;i<rows.length;i++){
        const r = rows[i];
        if(!r || !r.length) continue;
        const it = {
          Item: r[idx("Item")] || "",
          Kind: r[idx("Kind")] || "One-time",
          Amount: num(r[idx("Amount")]),
          Notes: r[idx("Notes")] || "",
          Active: (r[idx("Active")] || "Y")
        };
        if(String(it.Item).trim()) state.move.push(it);
      }
    }

    if(blocks.Budgets){
      const rows = parseCsv(blocks.Budgets);
      const head = rows[0] || [];
      const idx = (name)=>head.indexOf(name);
      for(let i=1;i<rows.length;i++){
        const r = rows[i];
        if(!r || !r.length) continue;
        const b = {
          Month: r[idx("Month")] || "",
          Type: r[idx("Type")] || "Expense",
          Category: r[idx("Category")] || "",
          BudgetAmount: num(r[idx("BudgetAmount")]),
          Notes: r[idx("Notes")] || "",
          Active: (r[idx("Active")] || "Y")
        };
        if(String(b.Month).trim() && String(b.Category).trim()) state.budgets.push(b);
      }
    }

    
    if(blocks.Categories){
      const rows = parseCsv(blocks.Categories);
      const head = rows[0] || [];
      const idx = (name)=>head.indexOf(name);
      for(let i=1;i<rows.length;i++){
        const r = rows[i];
        if(!r || !r.length) continue;
        const c = {
          Type: (r[idx("Type")] || "Expense"),
          Category: r[idx("Category")] || "",
          Subcategory: r[idx("Subcategory")] || "",
          Active: (r[idx("Active")] || "Y"),
          Tax: (r[idx("Tax")] || ""),
          TaxNote: (r[idx("TaxNote")] || "")
        };
        if(String(c.Category).trim()) state.categories.push(c);
      }
    }
if(blocks.Goals){
      const rows = parseCsv(blocks.Goals);
      const head = rows[0] || [];
      const idx = (name)=>head.indexOf(name);
      for(let i=1;i<rows.length;i++){
        const r = rows[i];
        if(!r || !r.length) continue;
        const g = {
          GoalName: r[idx("GoalName")] || "",
          TargetAmount: num(r[idx("TargetAmount")]),
          CurrentAmount: num(r[idx("CurrentAmount")]),
          Notes: r[idx("Notes")] || "",
          Active: (r[idx("Active")] || "Y")
        };
        if(String(g.GoalName).trim()) state.goals.push(g);
      }
    }

    if(blocks.Transactions){
      loadTransactionsCsv(blocks.Transactions, opts);
    }else{
      state.tx = [];
    }

    state.loaded = true;
    mergeAccountsByName();
  }

  function budgetFor(month, type, category){
    const m = String(month||"").trim();
    const typ = String(type||"Expense").trim();
    const cat = String(category||"").trim();
    const row = state.budgets.find(b =>
      String(b.Active||"Y").toUpperCase()!=="N" &&
      b.Month===m && b.Type===typ && String(b.Category||"").trim()===cat
    );
    return row ? num(row.BudgetAmount) : null;
  }

  function sumTxWhere(fn){
    let sum=0;
    for(const t of state.tx){
      if(fn(t)) sum += num(t.Amount);
    }
    return sum;
  }

  function spentFor(month, type, category){
    const m = String(month||"").trim();
    const typ = String(type||"Expense").trim();
    const cat = String(category||"").trim();
    return sumTxWhere(t => t.Month===m && t.Type===typ && String(t.Category||"").trim()===cat);
  }

  function startOfWeek(d){ return U.startOfWeek(d); }
  function endOfWeek(d){ return U.endOfWeek(d); }
  function isoToDate(s){ return U.isoToDate(s); }

  function parseKeywords(s){ return U.parseKeywords(s); }
  function catMatches(cat, keywords){ return U.catMatches(cat, keywords); }

  function evalWarningsForTx(tx, includeTx=true){
    const warns = [];
    if(!tx || tx.Type!=="Expense") return warns;

    const d = isoToDate(tx.Date || isoToday());
    const wS = startOfWeek(d);
    const wE = endOfWeek(d);

    const eatKeys = parseKeywords(state.rules.eatCats);
    const nonKeys = parseKeywords(state.rules.nonEssCats);

    const isEat = catMatches(tx.Category, eatKeys);
    const isNon = catMatches(tx.Category, nonKeys);
    const isImp = (String(tx.Impulse||"N").toUpperCase()==="Y");

    const dateISO = String(tx.Date||"").slice(0,10);

    const eatTotal = sumTxWhere(t=>{
      if(t.Type!=="Expense") return false;
      const td = isoToDate(t.Date);
      return td>=wS && td<=wE && catMatches(t.Category, eatKeys);
    }) + (includeTx && isEat ? num(tx.Amount) : 0);

    const impTotal = sumTxWhere(t=>{
      if(t.Type!=="Expense") return false;
      const td = isoToDate(t.Date);
      return td>=wS && td<=wE && String(t.Impulse||"N").toUpperCase()==="Y";
    }) + (includeTx && isImp ? num(tx.Amount) : 0);

    const nonDayTotal = sumTxWhere(t=>{
      if(t.Type!=="Expense") return false;
      return String(t.Date||"").slice(0,10)===dateISO && catMatches(t.Category, nonKeys);
    }) + (includeTx && isNon ? num(tx.Amount) : 0);

    const eatMax = num(state.rules.eatWeekly);
    const impMax = num(state.rules.impulseWeekly);
    const nonMax = num(state.rules.nonEssDaily);

    const money = U.money;

    if(eatMax>0 && isEat && eatTotal > eatMax){
      warns.push(`Eat Out weekly max exceeded: ${money(eatTotal)} > ${money(eatMax)} (week of ${wS.toISOString().slice(0,10)})`);
    }
    if(impMax>0 && isImp && impTotal > impMax){
      warns.push(`Impulse weekly max exceeded: ${money(impTotal)} > ${money(impMax)} (week of ${wS.toISOString().slice(0,10)})`);
    }
    if(nonMax>0 && isNon && nonDayTotal > nonMax){
      warns.push(`Non-essential daily max exceeded: ${money(nonDayTotal)} > ${money(nonMax)} (on ${dateISO})`);
    }

    const b = budgetFor(tx.Month, "Expense", tx.Category);
    if(b!==null && b>0){
      const spent = spentFor(tx.Month, "Expense", tx.Category);
      const after = spent + num(tx.Amount);
      if(after > b){
        warns.push(`Category budget exceeded (${tx.Category}): ${money(after)} > ${money(b)} (month ${tx.Month})`);
      }
    }

    const hasBud = state.budgets.some(x=>String(x.Active||"Y").toUpperCase()!=="N" && x.Month===tx.Month && x.Type==="Expense");
    if(hasBud){
      const totalBud = state.budgets
        .filter(x=>String(x.Active||"Y").toUpperCase()!=="N" && x.Month===tx.Month && x.Type==="Expense")
        .reduce((a,x)=>a+num(x.BudgetAmount),0);
      const spentBud = state.tx
        .filter(t=>t.Month===tx.Month && t.Type==="Expense")
        .reduce((a,t)=>a+num(t.Amount),0) + num(tx.Amount);
      if(totalBud>0 && spentBud > totalBud){
        warns.push(`Total monthly budgets exceeded: ${money(spentBud)} > ${money(totalBud)} (month ${tx.Month})`);
      }
    }

    return warns;
  }

  function applyFlagsForTx(tx, warnings, overridden=false){
    const flags = new Set(String(tx.Flags||"").split(",").map(x=>x.trim()).filter(Boolean));
    if(warnings && warnings.length) flags.add("overbudget");
    if(overridden) flags.add("override");
    tx.Flags = Array.from(flags).join(",");
  }

  function computedBalanceFor(accountName){
    const acc = state.accounts.find(a=>a.Account===accountName);
    const start = acc ? num(acc.StartBalance) : 0;
    const nature = String(acc?.Nature || "ASSET").toUpperCase();

    let delta = 0;
    for(const t of state.tx){
      if(t.Account!==accountName) continue;

      if(nature==="LIABILITY"){
        if(t.Type==="Expense") delta += num(t.Amount);
        if(t.Type==="Income") delta -= num(t.Amount);
      }else{
        if(t.Type==="Income") delta += num(t.Amount);
        if(t.Type==="Expense") delta -= num(t.Amount);
      }
    }
    return start + delta;
  }

  function _iso10(s){
    const x = String(s||"").trim();
    if(!x) return "";
    // already ISO
    if(/^\d{4}-\d{2}-\d{2}/.test(x)) return x.slice(0,10);
    // try Date parse
    const d = new Date(x);
    if(!isFinite(d.getTime())) return "";
    const yy = d.getFullYear();
    const mm = String(d.getMonth()+1).padStart(2,"0");
    const dd = String(d.getDate()).padStart(2,"0");
    return `${yy}-${mm}-${dd}`;
  }

  function _dayBefore(iso){
    const i = _iso10(iso);
    if(!i) return "";
    const d = new Date(i+"T00:00:00");
    d.setDate(d.getDate()-1);
    return d.toISOString().slice(0,10);
  }

  function computedBalanceForAsOf(accountName, endDateISO){
    const endISO = _iso10(endDateISO);
    if(!endISO) return computedBalanceFor(accountName);

    const acc = state.accounts.find(a=>a.Account===accountName);
    const start = acc ? num(acc.StartBalance) : 0;
    const nature = String(acc?.Nature || "ASSET").toUpperCase();

    let delta = 0;
    for(const t of state.tx){
      if(t.Account!==accountName) continue;
      const dt = _iso10(t.Date);
      if(!dt) continue;
      if(dt > endISO) continue;

      if(nature==="LIABILITY"){
        if(t.Type==="Expense") delta += num(t.Amount);
        if(t.Type==="Income") delta -= num(t.Amount);
      }else{
        if(t.Type==="Income") delta += num(t.Amount);
        if(t.Type==="Expense") delta -= num(t.Amount);
      }
    }
    return start + delta;
  }

  function reconcileLedger(accountName, startISO, endISO){
    const s = _iso10(startISO);
    const e = _iso10(endISO);
    const acc = state.accounts.find(a=>a.Account===accountName);
    const nature = String(acc?.Nature || "ASSET").toUpperCase();

    const openBal = computedBalanceForAsOf(accountName, _dayBefore(s));
    let running = openBal;

    const rows = (state.tx||[])
      .filter(t=>t.Account===accountName)
      .map(t=>({ t, d:_iso10(t.Date) }))
      .filter(x=>x.d && (!s || x.d>=s) && (!e || x.d<=e))
      .sort((a,b)=>a.d.localeCompare(b.d) || String(a.t.ID||"").localeCompare(String(b.t.ID||"")));

    const out = [];
    let incomeTotal = 0;
    let expenseTotal = 0;
    let netEffect = 0;
    for(const x of rows){
      const t = x.t;
      const amt = num(t.Amount);
      if(t.Type==="Income") incomeTotal += amt;
      if(t.Type==="Expense") expenseTotal += amt;
      let effect = 0;
      if(nature==="LIABILITY"){
        if(t.Type==="Expense") effect = +amt;
        else if(t.Type==="Income") effect = -amt;
      }else{
        if(t.Type==="Income") effect = +amt;
        else if(t.Type==="Expense") effect = -amt;
      }
      netEffect += effect;
      running = running + effect;
      out.push({
        Date: x.d,
        Type: String(t.Type||""),
        Category: String(t.Category||""),
        Payee: String(t.Payee||""),
        Amount: amt,
        Effect: effect,
        Running: running
      });
    }

    const expectedEnd = computedBalanceForAsOf(accountName, e);
    return { openBal, expectedEnd, rows: out, incomeTotal, expenseTotal, netEffect };
  }

  function daysInMonth(y, mIndex){ return U.daysInMonth(y,mIndex); }

  function nextBillDueDate(bill, fromDate){
    const dueDay = Math.max(1, Math.min(31, num(bill.DueDay)||1));
    let y = fromDate.getFullYear();
    let m = fromDate.getMonth();

    function makeDate(yy, mm){
      const dim = daysInMonth(yy, mm);
      const dd = Math.max(1, Math.min(dim, dueDay));
      const d = new Date(yy, mm, dd);
      d.setHours(0,0,0,0);
      return d;
    }

    let d = makeDate(y,m);
    const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    start.setHours(0,0,0,0);
    if(d < start){
      m += 1;
      if(m>11){ m=0; y+=1; }
      d = makeDate(y,m);
    }
    return d;
  }

  function billsDueNext7Days(){
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    start.setHours(0,0,0,0);
    const end = new Date(start);
    end.setDate(end.getDate()+7);
    end.setHours(23,59,59,999);

    return (state.bills||[])
      .filter(b=>String(b.Active||"Y").toUpperCase()!=="N")
      .map(b=>{
        const due = nextBillDueDate(b, start);
        return { ...b, _due: due, DueISO: due.toISOString().slice(0,10) };
      })
      .filter(x=>x._due>=start && x._due<=end)
      .sort((a,b)=>a._due-b._due);
  }

  function plannedRemainForMonth(month){
    const m = String(month||"").trim();
    const buds = state.budgets.filter(b=>b.Month===m && b.Type==="Expense" && String(b.Active||"Y").toUpperCase()!=="N");
    if(!buds.length) return 0;
    let remain = 0;
    for(const b of buds){
      const spent = spentFor(m, "Expense", b.Category);
      remain += (num(b.BudgetAmount) - spent);
    }
    return remain;
  }

  function buildMasterCsvText(){
    mergeAccountsByName();

    const settingsRows = [
      ["Key","Value"],
      ["Currency", state.settings.currency || "USD"],
      ["People", (state.settings.people||[]).join(", ")]
    ];

    const rulesRows = [
      ["Key","Value"],
      ["Mode", String(state.rules.mode||"BLOCK").toUpperCase()],
      ["EatCats", state.rules.eatCats || ""],
      ["EatWeekly", String(num(state.rules.eatWeekly))],
      ["ImpulseWeekly", String(num(state.rules.impulseWeekly))],
      ["NonEssCats", state.rules.nonEssCats || ""],
      ["NonEssDaily", String(num(state.rules.nonEssDaily))],
      ["InvestMonthly", String(num(state.grow.investMonthly))],
      ["InvestAccount", state.grow.investAccount || ""]
    ];

    const accRows = [
      ["Account","Owner","Type","Nature","StartBalance","BankBalance","Active"],
      ...state.accounts.map(a=>[
        a.Account||"",
        a.Owner||"",
        a.Type||"",
        String(a.Nature||"ASSET").toUpperCase()==="LIABILITY" ? "LIABILITY" : "ASSET",
        String(num(a.StartBalance)),
        (String(a.BankBalance??"").trim()==="" ? "" : String(num(a.BankBalance))),
        String(a.Active||"Y")
      ])
    ];

    const billRows = [
      ["BillName","Amount","DueDay","Account","AutoPay","Notes","Active"],
      ...state.bills.map(b=>[
        b.BillName||"",
        String(num(b.Amount)),
        String(Math.max(1, Math.min(31, num(b.DueDay)||1))),
        b.Account||"",
        String(b.AutoPay||"N"),
        b.Notes||"",
        String(b.Active||"Y")
      ])
    ];

    const moveRows = [
      ["Item","Kind","Amount","Notes","Active"],
      ...state.move.map(m=>[
        m.Item||"",
        (String(m.Kind||"One-time")==="Monthly" ? "Monthly" : "One-time"),
        String(num(m.Amount)),
        m.Notes||"",
        String(m.Active||"Y")
      ])
    ];

    const budRows = [
      ["Month","Type","Category","BudgetAmount","Notes","Active"],
      ...state.budgets.map(b=>[
        b.Month||"",
        b.Type||"Expense",
        b.Category||"",
        String(num(b.BudgetAmount)),
        b.Notes||"",
        String(b.Active||"Y")
      ])
    ];

    const catRows = [
      ["Type","Category","Subcategory","Active","Tax","TaxNote"],
      ...(() => {
        const out = [];
        const seen = new Set();
        const add = (Type, Category, Subcategory, Active, Tax, TaxNote) => {
          Type = (String(Type||"Expense")||"Expense").trim() || "Expense";
          Category = String(Category||"").trim();
          Subcategory = String(Subcategory||"").trim();
          if(!Category) return;
          const key = (Type+"|"+Category+"|"+Subcategory).toLowerCase();
          if(seen.has(key)) return;
          seen.add(key);
          out.push([Type, Category, Subcategory, String(Active||"Y"), String(Tax||""), String(TaxNote||"")]);
        };
        (state.categories||[]).forEach(c => add(c.Type, c.Category, c.Subcategory, c.Active, c.Tax, c.TaxNote));
        (state.tx||[]).forEach(t => add(t.Type||"Expense", t.Category, t.Subcategory, "Y", "", ""));
        out.sort((a,b)=>(a[0]+a[1]+a[2]).localeCompare(b[0]+b[1]+b[2]));
        return out;
      })()
    ];

    const goalRows = [
      ["GoalName","TargetAmount","CurrentAmount","Notes","Active"],
      ...state.goals.map(g=>[
        g.GoalName||"",
        String(num(g.TargetAmount)),
        String(num(g.CurrentAmount)),
        g.Notes||"",
        String(g.Active||"Y")
      ])
    ];

    const txRows = [
      DEFAULT_HEADER_TX,
      ...state.tx.map(t=>[
        t.ID||"",
        t.Date||"",
        t.Month||toMonth(t.Date),
        t.Type||"Expense",
        t.Person||"",
        t.Account||"",
        t.Category||"",
        t.Subcategory||"",
        t.Item||"",
        t.Payee||"",
        String(Math.abs(num(t.Amount))),
        t.Notes||"",
        (String(t.Impulse||"N").toUpperCase()==="Y" ? "Y" : "N"),
        t.ImpulseReason||"",
        (t.Scope||"Personal"),
        (t.Receipt||""),
        t.Flags||""
      ])
    ];

    const parts = [];
    parts.push("#TABLE:Settings\n" + U.toCsvNoBom(settingsRows));
    parts.push("#TABLE:Rules\n" + U.toCsvNoBom(rulesRows));
    parts.push("#TABLE:Accounts\n" + U.toCsvNoBom(accRows));
    parts.push("#TABLE:Bills\n" + U.toCsvNoBom(billRows));
    parts.push("#TABLE:Move\n" + U.toCsvNoBom(moveRows));
    parts.push("#TABLE:Budgets\n" + U.toCsvNoBom(budRows));
    parts.push("#TABLE:Categories\n" + U.toCsvNoBom(catRows));
    parts.push("#TABLE:Goals\n" + U.toCsvNoBom(goalRows));
    parts.push("#TABLE:Transactions\n" + U.toCsvNoBom(txRows));

    return "\uFEFF" + parts.join("\n\n");
  }

  function buildTxOnlyCsvText(){
    const txRows = [
      DEFAULT_HEADER_TX,
      ...state.tx.map(t=>[
        t.ID||"",
        (t.Date||isoToday()).slice(0,10),
        String(t.Month||"").trim() || toMonth(t.Date||isoToday()),
        (t.Type==="Income" ? "Income" : "Expense"),
        t.Person||"",
        t.Account||"",
        t.Category||"",
        t.Subcategory||"",
        t.Item||"",
        t.Payee||"",
        String(Math.abs(num(t.Amount))),
        t.Notes||"",
        (String(t.Impulse||"N").toUpperCase()==="Y" ? "Y" : "N"),
        t.ImpulseReason||"",
        (t.Scope||"Personal"),
        (t.Receipt||""),
        t.Flags||""
      ])
    ];
    return "\uFEFF" + U.toCsvNoBom(txRows);
  }

  function downloadMaster(filename){
    const name = filename || `BudgetHub_MASTER_${ymToday()}.csv`;
    if(typeof downloadText === "function") downloadText(name, buildMasterCsvText());
    else console.warn("[TYBH] util.downloadText missing");
  }

  function downloadTxOnly(filename){
    const name = filename || `BudgetHub_TX_${ymToday()}.csv`;
    if(typeof downloadText === "function") downloadText(name, buildTxOnlyCsvText());
    else console.warn("[TYBH] util.downloadText missing");
  }

  function buildGptPackText(month){
    if(!state.loaded) return "Import a CSV first.";
    const m = String(month||"").trim() || ymToday();
    const rows = m ? state.tx.filter(t=>t.Month===m) : [...state.tx];

    let income=0, expense=0;
    for(const r of rows){
      if(r.Type==="Income") income += num(r.Amount);
      if(r.Type==="Expense") expense += num(r.Amount);
    }
    const net = income-expense;

    const byCat = new Map();
    for(const r of rows){
      if(r.Type!=="Expense") continue;
      const k = String(r.Category||"(uncat)");
      byCat.set(k, (byCat.get(k)||0)+num(r.Amount));
    }
    const topCats = Array.from(byCat.entries()).sort((a,b)=>b[1]-a[1]).slice(0,15);

    const buds = state.budgets.filter(b=>b.Month===m && b.Type==="Expense" && String(b.Active||"Y").toUpperCase()!=="N");
    const budLines = buds.length ? buds.map(b=>{
      const spent = spentFor(m,"Expense",b.Category);
      const rem = num(b.BudgetAmount)-spent;
      return `- ${b.Category}: budget ${U.money(num(b.BudgetAmount))}, spent ${U.money(spent)}, remain ${U.money(rem)}`;
    }).join("\n") : "(no budgets)";

    const impRows = rows.filter(t=>t.Type==="Expense" && String(t.Impulse||"N").toUpperCase()==="Y");
    const impSum = impRows.reduce((a,t)=>a+num(t.Amount),0);

    const due = billsDueNext7Days().slice(0,12)
      .map(b=>`- ${b.BillName}: ${U.money(num(b.Amount))} due ${b.DueISO}${b.Account?(" ("+b.Account+")"):""}${String(b.AutoPay||"N").toUpperCase()==="Y"?" [autopay]":""}`)
      .join("\n") || "(none)";

    const recent = [...rows].sort((a,b)=> (b.Date||"").localeCompare(a.Date||"")).slice(0,20)
      .map(t=>`- ${t.Date} ${t.Type} ${U.money(num(t.Amount))} | ${t.Category}${t.Subcategory?("/"+t.Subcategory):""} | ${t.Payee||""} | ${t.Account||"—"} | ${t.Person||""}${String(t.Impulse||"N").toUpperCase()==="Y"?" | impulse":""}`)
      .join("\n");

    return (
`BUDGET DATA PACK
Currency: ${state.settings.currency}
Month: ${m}

SUMMARY
Income: ${U.money(income)}
Expense: ${U.money(expense)}
Net: ${U.money(net)}

TOP EXPENSE CATEGORIES
${topCats.length ? topCats.map(([k,v])=>`- ${k}: ${U.money(v)}`).join("\n") : "(none)"}

BUDGETS (ACTIVE)
${budLines}

IMPULSE
Count: ${impRows.length}
Total: ${U.money(impSum)}

BILLS DUE NEXT 7 DAYS
${due}

RECENT TRANSACTIONS (TOP 20)
${recent}

REQUEST
Give me clear, practical budgeting advice for this month. Focus on stopping impulse spending and staying within budgets.`
    );
  }

  // ✅ legacy alias name (some UI versions call buildGptPack)
  function buildGptPack(month){ return buildGptPackText(month); }


// Preflight for CSV import (no state mutation)
function preflightImport(text){
  text = stripBom(String(text||""));
  const blocks = parseMaster(text);
  const isMaster = !!Object.keys(blocks||{}).length;
  const counts = { tx:0, accounts:0, budgets:0, bills:0, goals:0, moves:0, settings:0, rules:0 };
  const blockRows = (blockText)=>{
    if(!blockText) return 0;
    const rows = parseCsv((String(blockText).trim()+"\n"));
    return Math.max(0, (rows.length||0) - 1);
  };

  // compute tx + dup count using loader but restore original tx
  const prevTx = state.tx;
  let txTotal = 0, txDedup = 0, txDup = 0;
  try{
    const txText = isMaster ? (blocks.Transactions || "") : text;
    loadTransactionsCsv(txText, {dedup:false});
    txTotal = state.tx.length;
    loadTransactionsCsv(txText, {dedup:true});
    txDedup = state.tx.length;
    txDup = Math.max(0, txTotal - txDedup);
  }catch(e){
    // keep 0; UI will show error on commit anyway
  }finally{
    state.tx = prevTx;
  }
  counts.tx = txTotal;

  if(isMaster){
    counts.accounts = blockRows(blocks.Accounts);
    counts.budgets  = blockRows(blocks.Budgets);
    counts.bills    = blockRows(blocks.Bills);
    counts.goals    = blockRows(blocks.Goals);
    counts.moves    = blockRows(blocks.Move);
    counts.settings = blocks.Settings ? 1 : 0;
    counts.rules    = blocks.Rules ? 1 : 0;
  }

  return {
    isMaster,
    counts,
    txDup,
    txDedupIfEnabled: txDedup
  };
}

  TYBH.core = {
    DEFAULT_HEADER_TX,
    state,
    resetDataArrays,
    allMonths,
    monthTx,
    mergeAccountsByName,
    loadFromMaster,
    preflightImport,
    loadTransactionsCsv,
    budgetFor,
    sumTxWhere,
    spentFor,
    evalWarningsForTx,
    applyFlagsForTx,
    computedBalanceFor,
    computedBalanceForAsOf,
    reconcileLedger,
    nextBillDueDate,
    billsDueNext7Days,
    plannedRemainForMonth,
    buildMasterCsvText,
    buildTxOnlyCsvText,
    downloadMaster,
    downloadTxOnly,
    buildGptPackText,
    buildGptPack
  };
})();
